import React, { useCallback, useState } from 'react';

import { Text, useMantineTheme } from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import BackButton from 'components/BackButton';
import HelpButton from 'components/HelpButton';
import InvoiceFilterBar, {
  InvoiceFilterKey,
  InvoiceReportTotal,
} from 'components/invoices/InvoiceFilterBar';
import InvoicesTable, { InvoicesTableSearchFilters } from 'components/invoices/InvoicesTable';
import useTotalRevenue from 'hooks/reports/revenue/useTotalRevenue';
import { useNavigate } from 'react-router-dom';
import { Invoice } from 'types';
import { formatDateForApi, getMonthRangeDatesMTD } from 'utils/date-functions';

// Status is hidden: the report hard-filters paid invoices by payment date.
const FILTERS: InvoiceFilterKey[] = ['paymentDateRange', 'productType', 'billingSources', 'paymentMethods'];

const TotalRevenue = (): JSX.Element => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const defaultDates = getMonthRangeDatesMTD();

  const initialSearchFiltersValue: InvoicesTableSearchFilters = {
    paymentDateRange: [defaultDates[0], defaultDates[1]],
    productType: null,
    billingSources: [],
    status: [] as Array<Invoice['status']>,
    paymentMethods: [],
  };

  const [searchFilters, setSearchFilters] = useState<InvoicesTableSearchFilters>(initialSearchFiltersValue);
  const [debouncedSearchFilters] = useDebouncedValue<InvoicesTableSearchFilters>(searchFilters, 250);
  const [orderingValue, setOrderingValue] = useState('dueDate');

  const total = useTotalRevenue({
    ...searchFilters,
    paymentDateStart: formatDateForApi(searchFilters.paymentDateRange?.[0]) ?? null,
    paymentDateEnd: formatDateForApi(searchFilters.paymentDateRange?.[1]) ?? null,
  });

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  // The total is meaningless without both ends of the range, so it stays hidden
  // until the picker has one.
  const hasFullRange = !!(searchFilters.paymentDateRange?.[0] && searchFilters.paymentDateRange?.[1]);
  const hasProductFiltering = !!(searchFilters.productType || searchFilters.billingSources?.length);
  const showPartialRefundWarning = !!total.data?.data.hasPartiallyRefunded && hasProductFiltering;

  return (
    <>
      <InvoiceFilterBar
        source='total-revenue'
        title='Total Revenue'
        filters={FILTERS}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        initialSearchFiltersValue={initialSearchFiltersValue}
        orderingValue={orderingValue}
        csvReportType='total-revenue'
        leftSection={<BackButton handleOnClick={handleBackClick} />}
        titleDataCy='totalRevenuePageHeader'
        titleSuffix={
          <>
            <InvoiceReportTotal query={total} color='green' isReady={hasFullRange} />
            {showPartialRefundWarning && (
              <HelpButton
                width={400}
                position={isDesktop ? 'right' : 'bottom'}
                size='sm'
                actionIconProps={{ color: 'orange' }}
              >
                <Text size='sm' sx={{ whiteSpace: 'normal' }}>
                  Some invoices included in this total have partial refunds. Revenue values may be affected.
                </Text>
              </HelpButton>
            )}
          </>
        }
      />

      <InvoicesTable searchFilters={debouncedSearchFilters} setOrderingValue={setOrderingValue} />
    </>
  );
};

export default TotalRevenue;
